import { Link } from "react-router-dom";
import {
  Linkedin,
  Twitter,
  Instagram,
  Youtube,
  Facebook,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { FOOTER_LOGO_URL } from "../../data/site";

const SERVICE_LINKS = [
  { label: "App Store Optimization", href: "/aso" },
  { label: "User Acquisition", href: "/user-acquisition" },
  { label: "Influencer Marketing", href: "/influencer-marketing" },
  { label: "Content Marketing & PR", href: "/content-marketing" },
  { label: "App Retention", href: "/app-retention" },
  { label: "Analytics & Attribution", href: "/analytics" },
  { label: "Advanced Paid Marketing", href: "/advanced-paid-marketing" },
  { label: "Ratings & Reviews", href: "/ratings-reviews" },
];

const PREMIUM_LINKS = [
  { label: "GTM Strategy", href: "/gtm-strategy" },
  { label: "Creative Production", href: "/creative-production" },
  { label: "International Expansion", href: "/international-expansion" },
  { label: "Consultancy / Audit", href: "/consultancy" },
];

const RESOURCE_LINKS = [
  { label: "Free ASO Audit", href: "/free-aso-audit" },
  { label: "App vs App Comparison", href: "/compare-apps" },
  { label: "Keyword Rank Checker", href: "/keyword-rank-checker" },
  { label: "Review Analyzer", href: "/review-analyzer" },
  { label: "ASO Monitor", href: "/aso-monitor" },
  { label: "ASO Reports", href: "/aso-reports" },
  { label: "Revenue Estimator", href: "/resources/app-revenue-estimator" },
  { label: "Discovery Questionnaire", href: "/discovery" },
  { label: "Build Your Plan", href: "/build-your-plan" },
  { label: "ASO Score Checker", href: "/resources/aso-score-checker" },
  { label: "ROI Calculator", href: "/resources/roi-calculator" },
  { label: "App LTV Calculator", href: "/resources/app-ltv-calculator" },
  { label: "App Launch Checklist", href: "/resources/app-launch-checklist" },
  { label: "Keyword Research Template", href: "/resources/keyword-research-template" },
  { label: "Agency Comparisons", href: "/compare" },
  { label: "Media Kit", href: "/media-kit" },
  { label: "All Resources", href: "/resources" },
  { label: "Blog", href: "/blog" },
];

const COMPANY_LINKS = [
  { label: "About AppFillip", href: "/about" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Careers", href: "/careers" },
  { label: "Partners", href: "/partners" },
  { label: "Contact", href: "/contact" },
];

const MARKETS = [
  { label: "USA", code: "us", flag: "🇺🇸" },
  { label: "UK", code: "uk", flag: "🇬🇧" },
  { label: "Australia", code: "au", flag: "🇦🇺" },
  { label: "UAE", code: "uae", flag: "🇦🇪" },
  { label: "Canada", code: "ca", flag: "🇨🇦" },
  { label: "Germany", code: "de", flag: "🇩🇪" },
  { label: "France", code: "fr", flag: "🇫🇷" },
  { label: "Japan", code: "jp", flag: "🇯🇵" },
  { label: "Singapore", code: "sg", flag: "🇸🇬" },
  { label: "South Korea", code: "kr", flag: "🇰🇷" },
  { label: "Thailand", code: "th", flag: "🇹🇭" },
  { label: "Spain", code: "es", flag: "🇪🇸" },
  { label: "Brazil", code: "br", flag: "🇧🇷" },
  { label: "India", code: "in", flag: "🇮🇳" },
];

const CATEGORIES = [
  { label: "Gaming", slug: "gaming-app-marketing" },
  { label: "FinTech", slug: "fintech-app-marketing" },
  { label: "Health & Fitness", slug: "health-fitness-app-marketing" },
  { label: "EdTech", slug: "education-app-marketing" },
  { label: "eCommerce", slug: "ecommerce-app-marketing" },
  { label: "Food Delivery", slug: "food-delivery-app-marketing" },
];

const SOCIAL = [
  { Icon: Linkedin, href: "https://linkedin.com/company/appfillip", label: "LinkedIn" },
  { Icon: Twitter, href: "https://twitter.com/appfillip", label: "Twitter" },
  { Icon: Instagram, href: "https://instagram.com/appfillip", label: "Instagram" },
  { Icon: Youtube, href: "https://youtube.com/@appfillip", label: "YouTube" },
  { Icon: Facebook, href: "https://facebook.com/appfillip", label: "Facebook" },
];

export default function Footer() {
  return (
    <footer
      className="relative bg-charcoal text-white pt-20 pb-8 overflow-hidden"
      data-testid="site-footer"
    >
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-40 pointer-events-none" />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 pointer-events-none select-none flex items-end overflow-hidden"
      >
        <span className="watermark text-[28vw] leading-none text-white/[0.04] -mb-12">
          AppFillip
        </span>
      </div>

      <div className="container relative">
        {/* ─── Top: Brand + 4 link columns ───────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-10 pb-12 border-b border-white/10">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-3 lg:col-span-4">
            <Link
              to="/"
              className="inline-flex items-center"
              data-testid="footer-logo"
              aria-label="AppFillip — home"
            >
              <img
                src={FOOTER_LOGO_URL}
                alt="AppFillip — App Store Optimization Agency"
                width="200"
                height="60"
                className="h-14 w-auto object-contain"
              />
            </Link>
            <p className="mt-5 text-zinc-400 max-w-sm leading-relaxed text-[15px]">
              The Global App Store Optimization & App Marketing Agency.
              Ranking apps from California to Singapore — across 15+ markets.
            </p>

            {/* Social */}
            <div className="mt-5 flex items-center gap-2">
              {SOCIAL.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 grid place-items-center border border-white/15 hover:bg-electric hover:border-electric transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Premium services — tag strip */}
            <div className="mt-7 pt-6 border-t border-white/10">
              <div className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-electric text-white text-[10px] font-black tracking-[0.2em] px-2 py-1 mb-3">
                <Sparkles className="w-2.5 h-2.5" />
                PREMIUM
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PREMIUM_LINKS.map((l) => (
                  <Link
                    key={l.href}
                    to={l.href}
                    className="inline-flex items-center text-[11px] uppercase tracking-widest font-bold text-zinc-300 hover:text-white border border-white/10 hover:border-amber-400 px-2 py-1 transition-colors"
                    data-testid={`footer-premium-tag-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Services</p>
            <ul className="space-y-2.5">
              {SERVICE_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-[13px] text-zinc-300 hover:text-electric transition-colors"
                    data-testid={`footer-service-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

          </div>

          {/* Resources */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Resources</p>
            <ul className="space-y-2.5">
              {RESOURCE_LINKS.map((l) => {
                const isAllResources = l.href === "/resources";
                return (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      className={`text-[13px] transition-colors inline-flex items-center gap-1 ${
                        isAllResources
                          ? "text-electric font-semibold hover:gap-2"
                          : "text-zinc-300 hover:text-electric"
                      }`}
                      data-testid={`footer-resource-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {l.label}
                      {isAllResources && <ArrowRight className="w-3 h-3 transition-all" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <p className="text-overline text-zinc-500 mb-5">Company</p>
            <ul className="space-y-2.5">
              {COMPANY_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-[13px] text-zinc-300 hover:text-electric transition-colors"
                    data-testid={`footer-company-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

          </div>
        </div>

        {/* ─── Markets + Categories chip strip ─── */}
        <div className="py-7 border-b border-white/10 grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-overline text-zinc-500 mb-3">Markets we serve</p>
            <div className="flex flex-wrap gap-1.5">
              {MARKETS.map((m) => (
                <Link
                  key={m.code}
                  to={`/${m.code}`}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white border border-white/10 hover:border-electric px-2 py-1 transition-colors"
                  data-testid={`footer-market-${m.code}`}
                >
                  <span aria-hidden className="text-sm leading-none">{m.flag}</span>
                  {m.label}
                </Link>
              ))}
              <Link
                to="/markets"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-electric hover:text-white px-2 py-1"
                data-testid="footer-market-view-all"
              >
                All 15+ <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>

          <div>
            <p className="text-overline text-zinc-500 mb-3">App categories</p>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/app-marketing/${c.slug}`}
                  className="text-[11px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white border border-white/10 hover:border-electric px-2 py-1 transition-colors"
                  data-testid={`footer-category-${c.slug}`}
                >
                  {c.label}
                </Link>
              ))}
              <Link
                to="/app-categories"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-electric hover:text-white px-2 py-1"
                data-testid="footer-category-view-all"
              >
                All categories <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>

        {/* ─── Bottom legal row ─── */}
        <div className="pt-7 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-zinc-500">
          <p>
            © {new Date().getFullYear()} AppFillip · Lumotis Digital Media Pvt. Ltd. (est. 2017) · Spark App Growth.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-electric" data-testid="footer-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-electric" data-testid="footer-terms">
              Terms
            </Link>
            <Link to="/cookies" className="hover:text-electric" data-testid="footer-cookies">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
